'use client';

import * as React from 'react';
import { Field } from '@chakra-ui/react';
import { FiCalendar } from 'react-icons/fi';

import { Box } from './box';
import { Input } from './input';
import { HStack, VStack } from './stack';
import { Text } from './text';
import { Button } from './button';
import { Popover } from './popover';
import { SimpleGrid } from './simple-grid';

export interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

/**
 * Translations for DateRangePicker component
 */
export interface DateRangePickerTranslations {
  selectDateRange?: string;
  quickSelection?: string;
  customRange?: string;
  startDate?: string;
  endDate?: string;
  from?: string;
  until?: string;
  apply?: string;
  cancel?: string;
  today?: string;
  yesterday?: string;
  last7Days?: string;
  last30Days?: string;
  thisMonth?: string;
  lastMonth?: string;
  thisWeek?: string;
  lastWeek?: string;
}

const defaultTranslations: DateRangePickerTranslations = {
  selectDateRange: 'Select date range',
  quickSelection: 'Quick Selection',
  customRange: 'Custom Range',
  startDate: 'Start Date',
  endDate: 'End Date',
  from: 'From {date}',
  until: 'Until {date}',
  apply: 'Apply',
  cancel: 'Cancel',
  today: 'Today',
  yesterday: 'Yesterday',
  last7Days: 'Last 7 days',
  last30Days: 'Last 30 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
  thisWeek: 'This week',
  lastWeek: 'Last week',
};

export interface DateRangePickerProps {
  /**
   * Current date range value
   */
  value?: DateRange;

  /**
   * Callback when date range changes
   */
  onChange?: (range: DateRange) => void;

  /**
   * Label for the field
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;

  /**
   * Error message
   */
  error?: string;

  /**
   * Helper text
   */
  hint?: string;

  /**
   * Minimum date (ISO string or Date)
   */
  minDate?: string | Date;

  /**
   * Maximum date (ISO string or Date)
   */
  maxDate?: string | Date;

  /**
   * Whether the field is disabled
   */
  disabled?: boolean;

  /**
   * Whether the field is required
   */
  required?: boolean;

  /**
   * Predefined ranges to show
   */
  predefinedRanges?: Array<{
    label: string;
    getValue: () => DateRange;
  }>;

  /**
   * Container className
   */
  containerClassName?: string;

  /**
   * Input size
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Locale for date formatting (e.g., 'en-US', 'tr-TR')
   */
  locale?: string;

  /**
   * Custom translations for UI strings
   */
  translations?: DateRangePickerTranslations;
}

// Helper function to format date for display
const formatDateWithLocale = (date: string | null, locale: string = 'en-US'): string => {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Function to create predefined ranges with translations
const createPredefinedRanges = (t: DateRangePickerTranslations) => [
  {
    label: t.today || 'Today',
    getValue: () => {
      const today = new Date().toISOString().split('T')[0];
      return { startDate: today, endDate: today };
    },
  },
  {
    label: t.yesterday || 'Yesterday',
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const date = yesterday.toISOString().split('T')[0];
      return { startDate: date, endDate: date };
    },
  },
  {
    label: t.last7Days || 'Last 7 days',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    },
  },
  {
    label: t.last30Days || 'Last 30 days',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    },
  },
  {
    label: t.thisMonth || 'This month',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    },
  },
  {
    label: t.lastMonth || 'Last month',
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
      };
    },
  },
];

const DateRangePicker = React.forwardRef<HTMLDivElement, DateRangePickerProps>(
  (
    {
      value = { startDate: null, endDate: null },
      onChange,
      label,
      placeholder,
      error,
      hint,
      minDate,
      maxDate,
      disabled,
      required,
      predefinedRanges,
      containerClassName,
      size = 'md',
      locale = 'en-US',
      translations,
    },
    ref
  ) => {
    // Merge translations with defaults
    const t = React.useMemo(() => ({ ...defaultTranslations, ...translations }), [translations]);

    // Create localized predefined ranges
    const localizedPredefinedRanges = React.useMemo(
      () => predefinedRanges || createPredefinedRanges(t),
      [predefinedRanges, t]
    );

    // Helper to format date with current locale
    const formatDate = React.useCallback(
      (date: string | null) => formatDateWithLocale(date, locale),
      [locale]
    );
    const [isOpen, setIsOpen] = React.useState(false);
    const [tempRange, setTempRange] = React.useState<DateRange>(value);

    // Update temp range when value changes (compare by content, not reference)
    React.useEffect(() => {
      setTempRange((prev) => {
        // Only update if values actually changed
        if (prev.startDate !== value.startDate || prev.endDate !== value.endDate) {
          return { startDate: value.startDate, endDate: value.endDate };
        }
        return prev;
      });
    }, [value.startDate, value.endDate]);

    // Convert min/max to string format
    const minDateStr = React.useMemo(() => {
      if (!minDate) return undefined;
      const date = minDate instanceof Date ? minDate : new Date(minDate);
      return date.toISOString().split('T')[0];
    }, [minDate]);

    const maxDateStr = React.useMemo(() => {
      if (!maxDate) return undefined;
      const date = maxDate instanceof Date ? maxDate : new Date(maxDate);
      return date.toISOString().split('T')[0];
    }, [maxDate]);

    // Handle apply
    const handleApply = () => {
      if (onChange) {
        onChange(tempRange);
      }
      setIsOpen(false);
    };

    // Handle cancel
    const handleCancel = () => {
      setTempRange(value);
      setIsOpen(false);
    };

    // Handle predefined range selection
    const handlePredefinedRange = (getRangeValue: () => DateRange) => {
      const range = getRangeValue();
      setTempRange(range);
      if (onChange) {
        onChange(range);
      }
      setIsOpen(false);
    };

    // Format display value
    const displayValue = React.useMemo(() => {
      if (!value.startDate && !value.endDate) {
        return '';
      }
      if (value.startDate && value.endDate) {
        return `${formatDate(value.startDate)} - ${formatDate(value.endDate)}`;
      }
      if (value.startDate) {
        const fromText = t.from || 'From {date}';
        return fromText.replace('{date}', formatDate(value.startDate));
      }
      if (value.endDate) {
        const untilText = t.until || 'Until {date}';
        return untilText.replace('{date}', formatDate(value.endDate));
      }
      return '';
    }, [value, formatDate, t]);

    const invalid = !!error;

    return (
      <Field.Root
        ref={ref}
        {...(invalid ? { invalid: true } : {})}
        className={containerClassName}
        disabled={disabled}
      >
        {/* Label */}
        {label && (
          <Box as="label" fontSize="sm" fontWeight="medium" marginBottom="8px" display="block">
            {label}
            {required && (
              <Box as="span" color="#EF4444" marginLeft={1}>
                *
              </Box>
            )}
          </Box>
        )}

        {/* Date Range Input */}
        <Popover.Root open={isOpen} onOpenChange={(open: boolean) => setIsOpen(open)}>
          <Popover.Trigger>
            <Box
              as="button"
              position="relative"
              width="100%"
              cursor={disabled ? 'not-allowed' : 'pointer'}
              opacity={disabled ? 0.5 : 1}
              _focus={{ outline: 'none' }}
              aria-label={t.selectDateRange}
            >
              <Input
                value={displayValue}
                placeholder={placeholder || t.selectDateRange}
                readOnly
                disabled={disabled}
                inputSize={size}
                paddingRight={10}
                style={{ pointerEvents: 'none' }}
              />
              <Box
                position="absolute"
                right={3}
                top="50%"
                transform="translateY(-50%)"
                color="#9CA3AF"
                pointerEvents="none"
              >
                <FiCalendar />
              </Box>
            </Box>
          </Popover.Trigger>

          <Popover.Content width="320px">
            <Box padding={4}>
              <VStack gap={4} align="stretch">
                {/* Predefined Ranges */}
                <VStack gap={2} align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                    {t.quickSelection}
                  </Text>
                  <SimpleGrid columns={2} gap={2}>
                    {localizedPredefinedRanges.map((range) => (
                      <Button
                        key={range.label}
                        size="sm"
                        variant="outline"
                        onClick={() => handlePredefinedRange(range.getValue)}
                      >
                        {range.label}
                      </Button>
                    ))}
                  </SimpleGrid>
                </VStack>

                {/* Custom Range */}
                <VStack gap={3} align="stretch">
                  <Text fontSize="sm" fontWeight="medium" color="fg.muted">
                    {t.customRange}
                  </Text>

                  <HStack gap={2}>
                    <VStack gap={1} flex={1}>
                      <Text fontSize="xs" color="fg.muted">
                        {t.startDate}
                      </Text>
                      <Input
                        type="date"
                        inputSize="sm"
                        value={tempRange.startDate || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setTempRange((prev) => ({
                            ...prev,
                            startDate: e.target.value || null,
                          }))
                        }
                        min={minDateStr}
                        max={maxDateStr}
                      />
                    </VStack>

                    <VStack gap={1} flex={1}>
                      <Text fontSize="xs" color="fg.muted">
                        {t.endDate}
                      </Text>
                      <Input
                        type="date"
                        inputSize="sm"
                        value={tempRange.endDate || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setTempRange((prev) => ({
                            ...prev,
                            endDate: e.target.value || null,
                          }))
                        }
                        min={tempRange.startDate || minDateStr}
                        max={maxDateStr}
                      />
                    </VStack>
                  </HStack>
                </VStack>

                {/* Actions */}
                <HStack gap={2} justifyContent="flex-end">
                  <Button size="sm" variant="ghost" onClick={handleCancel}>
                    {t.cancel}
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={handleApply}
                    disabled={!tempRange.startDate && !tempRange.endDate}
                  >
                    {t.apply}
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Popover.Content>
        </Popover.Root>

        {/* Error Message */}
        {error && (
          <Box fontSize="sm" color="#EF4444" marginTop={1}>
            {error}
          </Box>
        )}

        {/* Hint Message */}
        {hint && !error && (
          <Box fontSize="sm" color="#6B7280" marginTop={1}>
            {hint}
          </Box>
        )}
      </Field.Root>
    );
  }
);

DateRangePicker.displayName = 'DateRangePicker';

export { DateRangePicker };
